import * as React from "react"
import { ToggleGroup as ToggleGroupPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

export interface SegmentedControlOption<T extends string> {
  value: T
  label: React.ReactNode
  icon?: React.ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" | "false" }>
  disabled?: boolean
  /** Accessible name when `label` is not plain text (icon-only segments). */
  "aria-label"?: string
}

export interface SegmentedControlProps<T extends string> {
  "aria-label": string
  value: T
  onValueChange: (value: T) => void
  /**
   * Called for every segment click, including a click on the already-selected segment (which
   * does not fire `onValueChange`). Use it for side effects such as closing a popover.
   */
  onItemClick?: (value: T) => void
  options: ReadonlyArray<SegmentedControlOption<T>>
  size?: "compact" | "sm"
  disabled?: boolean
  className?: string
}

const TRACK_SIZE = {
  compact: "h-7",
  sm: "h-8",
} as const

const ITEM_SIZE = {
  compact: "px-2 text-xs [&_svg:not([class*='size-'])]:size-3.5",
  sm: "px-2.5 text-xs [&_svg:not([class*='size-'])]:size-4",
} as const

/**
 * Single-select segmented control (view modes, units, filters). Built on Radix ToggleGroup:
 * arrow keys move between segments, the selected segment is `role="radio"` + `aria-checked`, and
 * clicking the selected segment never clears the value.
 */
export function SegmentedControl<T extends string>({
  "aria-label": ariaLabel,
  value,
  onValueChange,
  onItemClick,
  options,
  size = "sm",
  disabled,
  className,
}: SegmentedControlProps<T>) {
  return (
    <ToggleGroupPrimitive.Root
      type="single"
      role="radiogroup"
      aria-label={ariaLabel}
      value={value}
      onValueChange={(next) => {
        // Radix allows deselecting a single toggle group; a segmented control always has a value.
        if (next) onValueChange(next as T)
      }}
      disabled={disabled}
      data-slot="segmented-control"
      className={cn(
        "inline-flex w-fit shrink-0 items-stretch gap-0.5 rounded-md border bg-muted/50 p-0.5",
        TRACK_SIZE[size],
        className
      )}
    >
      {options.map(({ value: optionValue, label, icon: Icon, disabled: optionDisabled, ...rest }) => (
        <ToggleGroupPrimitive.Item
          key={optionValue}
          value={optionValue}
          disabled={optionDisabled}
          aria-label={rest["aria-label"]}
          onClick={onItemClick ? () => onItemClick(optionValue) : undefined}
          data-slot="segmented-control-item"
          className={cn(
            "inline-flex items-center justify-center gap-1.5 rounded-sm font-semibold whitespace-nowrap text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-primary/15 data-[state=on]:text-primary data-[state=on]:ring-1 data-[state=on]:ring-inset data-[state=on]:ring-primary/40 [&_svg]:pointer-events-none [&_svg]:shrink-0",
            ITEM_SIZE[size]
          )}
        >
          {Icon ? <Icon aria-hidden="true" /> : null}
          {label}
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  )
}
